import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';

type IntercomFn = (
  command: string,
  settings?: Record<string, unknown>,
) => void;

// Intercom 是 GTM 在執行期注入的，程式碼裡沒有 SDK，只能從 window 拿
const getIntercom = (): IntercomFn | undefined =>
  (window as { Intercom?: IntercomFn }).Intercom;

const RETRY_INTERVAL_MS = 1000;
const MAX_RETRIES = 30;

/**
 * 手機版付款流程的「下一步」按鈕會被右下角 Intercom launcher 蓋住，
 * 進到頁面時把 launcher 收起來，離開時還原。
 * 只在手機寬度（< md）生效，桌機不受影響。
 */
export const useHideIntercomLauncher = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    let cancelled = false;
    let retries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const setLauncherHidden = (hidden: boolean): boolean => {
      const intercom = getIntercom();
      if (typeof intercom !== 'function') {
        return false;
      }
      intercom('update', { hide_default_launcher: hidden });
      return true;
    };

    // GTM 非同步載入 Intercom，進頁面時可能還不存在 → 輪詢等它載好
    const tryHide = () => {
      if (cancelled) {
        return;
      }
      if (!setLauncherHidden(true) && retries < MAX_RETRIES) {
        retries += 1;
        timer = setTimeout(tryHide, RETRY_INTERVAL_MS);
      }
    };
    tryHide();

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      setLauncherHidden(false);
    };
  }, [isMobile]);
};

export default useHideIntercomLauncher;
